import {
  useSearchQueryFilter,
  useSearchTypeFromRoute,
  useTrackEvents,
} from '@aibees/elephant/hooks';
import { classnames } from '@aibees/shared/utils';
import { Box } from '@mui/material';
import { memo, useState } from 'react';
import { FilterSection } from '../../components/filter-section';
import { useFilterSections } from '../../hooks/use-filter-sections';
import { findOpenFilterItems } from '../../utils';
import { CheckboxGroupFilterItem } from './components/checkbox-group-filter-item';
import { useOpenedItems } from './hooks/use-opened-items';
import * as SX from './sx';
import type { CheckboxGroupFilterProps as Props } from './types';

export const CheckboxGroupFilter = memo(
  ({ className, children, ...filter }: Props) => {
    const { params } = useSearchQueryFilter({ select: [filter.field!] });
    const searchType = useSearchTypeFromRoute();
    const { track } = useTrackEvents();
    const [open, setOpen] = useState<string[]>(findOpenFilterItems(filter));
    const [openDirty, setOpenDirty] = useState<boolean>(false);

    const { mainItems, moreItems } = useFilterSections(filter);

    const [mainOpened, moreOpened] = useOpenedItems(
      params,
      filter,
      mainItems,
      moreItems
    );

    const handleChange = (name: string, checked: boolean) => {
      track('FILTER', {
        filterName: filter.name,
        filterType: filter.type,
        searchType,
      });
      let draft;

      if (checked) draft = [...open, ...[name]];
      else draft = open.filter((x) => x !== name);
      setOpen(draft);
      if (!openDirty) setOpenDirty(true);
    };

    return (
      <Box
        className={classnames('CheckboxGroupFilter', className)}
        display={'flex'}
        flexDirection={'column'}
        gap={1}
        sx={SX.root}
        key={filter.name}
      >
        {mainItems?.map(({ key, open: o, ...item }) => (
          <CheckboxGroupFilterItem
            onChange={(e, checked) => handleChange(item.field!, checked)}
            key={key}
            open={
              open.includes(item.field!) ||
              (!openDirty && mainOpened?.includes(item.field!))
            }
            {...item}
          />
        ))}
        {moreItems?.length ? (
          <FilterSection open={!!moreOpened?.length} title="More Filters">
            {moreItems?.map(({ key, open: o, ...item }) => (
              <CheckboxGroupFilterItem
                onChange={(e, checked) => handleChange(item.field!, checked)}
                key={key}
                open={
                  open.includes(item.field!) ||
                  moreOpened?.includes(item.field!)
                }
                {...item}
              />
            ))}
          </FilterSection>
        ) : null}
      </Box>
    );
  }
);

CheckboxGroupFilter.displayName = 'CheckboxGroupFilter';
